import { Currency } from 'constants/currencies'
import { useShippingCosts } from 'hooks/useShippingFee'
import useTranslation from 'next-translate/useTranslation'
import { TradeType } from 'types'
import { isUK } from 'utils/currencies'
import { Edition, Fees } from 'utils/data'
import { getDhlFee } from 'utils/fees'

export type TradeFee = {
  name: string
  value: number | string
  currency?: Currency
}

type Payload = {
  /**
   * The value of the current trade. SHOULD BE IN USER CURRENCY, IN DOLLARS/EUR/ETC, NOT CENTS.
   */
  tradeValue: number
  /**
   * The value of the current trade in GBP, used to determine the shipping cost. SHOULD BE IN DOLLARS/EUR/ETC, NOT CENTS.
   */
  tradeValueGbp: number
  fees: Fees
  tradeType?: TradeType
  DHLInsurance?: boolean
  edition?: Edition
  country?: string
}

export const useTradeFees = ({
  tradeValue,
  tradeValueGbp,
  fees,
  tradeType,
  DHLInsurance,
  edition,
  country,
}: Payload) => {
  const { t } = useTranslation('review_trade_form')
  const { domesticShippingCost, shippingCost } = useShippingCosts({
    buyingPrice: tradeValue * 100,
    buyingPriceGbp: tradeValueGbp * 100,
    shipmentCategory: edition?.shipmentCategory,
    framed: edition?.artworkDetails?.features?.framed,
    galleryName: edition?.artworkDetails?.gallery,
    shipmentWeightOverride: edition?.shipmentWeightOverride,
    medium: edition?.artworkDetails.medium,
    size: edition?.artworkDetails.size,
    countryCode: country,
    tradeType,
  })

  const dhlFee = getDhlFee(tradeValue, fees.dhlInsuranceFee)

  let tradeFees: TradeFee[] = []

  if (tradeType === 'bid' || tradeType === 'buy') {
    tradeFees = [
      {
        name: t('shipping'),
        value: isUK(country) ? domesticShippingCost : shippingCost,
      },
      { name: t('processing_fee'), value: tradeValue * fees.processFee },
    ]
  } else {
    tradeFees = [
      {
        name: `${t('platform_fee')} (${fees.applicationFee * 100}%)`,
        value: -1 * tradeValue * fees.applicationFee,
      },
      {
        name: `${t('artist_royalties')} (${fees.royalitiesFee * 100}%)`,
        value: -1 * tradeValue * fees.royalitiesFee,
      },
    ]

    if (DHLInsurance) {
      tradeFees.push({
        name: t('dhl_insurance'),
        value: -1 * dhlFee,
      })
    }
  }

  return {
    tradeFees,
    total: tradeFees.reduce((prev, curr) => {
      if (typeof curr.value !== 'number') return prev
      return prev + curr.value
    }, tradeValue),
  }
}
